import logoFull from "arquivos/logo/logopadrao2-sem fundo.png";
import logoFullOnDark from "arquivos/logo/logopadrao-sem fundo.png";
import logoIcon from "arquivos/logo/icone.png";
import gabrielProfile from "assets/images/studio-edifique/gabriel-profile.svg";
import sabrinaProfile from "assets/images/studio-edifique/sabrina-profile.svg";
import imageManifest from "studio-edifique/generated/images.json";
import landingContent from "studio-edifique/landingContent.json";
import siteConfig from "studio-edifique/siteConfig.json";

const SITE_URL = siteConfig.siteUrl;
const imageBySource = Object.fromEntries(
  Object.values(imageManifest).map((image) => [image.src, image])
);

export const getImageProperties = (src) => {
  const image = imageBySource[src];
  return image ? { srcSet: image.srcSet, width: image.width, height: image.height } : {};
};

// Match the background and preload at the same viewport breakpoint, avoiding a second download.
export const getMobileBackgroundImage = (src) => {
  const image = imageBySource[src];
  return image?.srcSet.split(", ")[0].split(" ")[0] || src;
};

const compareStrings = (firstValue, secondValue) =>
  firstValue.localeCompare(secondValue, "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });

const normalizeAlt = (projectTitle, fileName) =>
  `${projectTitle} - ${fileName
    .replace(/\.(png|jpe?g|webp)$/i, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()}`;

const getGalleryForFolder = (folderName, projectTitle) =>
  Object.keys(imageManifest)
    .filter((key) => key.startsWith(`${folderName}/`))
    .sort(compareStrings)
    .map((key) => {
      const fileName = key.split("/").pop();

      return {
        src: imageManifest[key].src,
        alt: normalizeAlt(projectTitle, fileName),
        fileName,
      };
    });

const getImageFromFolder = (folderName, fileName) => {
  return imageManifest[`${folderName}/${fileName}`]?.src || null;
};

const createProject = ({ folderName, title, coverFile, fallbackImage = logoIcon, ...project }) => {
  const gallery = getGalleryForFolder(folderName, title);
  const cover =
    (coverFile && getImageFromFolder(folderName, coverFile)) || gallery[0]?.src || fallbackImage;
  const detail = landingContent.projects.find((item) => item.folderName === folderName);

  return {
    ...project,
    folderName,
    title,
    cover,
    gallery,
    imageCount: gallery.length,
    path: detail ? `/projetos/${detail.slug}` : undefined,
  };
};

export const getAssetUrl = (asset) => {
  if (!asset) {
    return undefined;
  }

  if (/^https?:\/\//i.test(asset)) {
    return asset;
  }

  return new URL(asset, SITE_URL).toString();
};

export const getSiteUrl = (path = "/") => {
  return new URL(path, SITE_URL).toString();
};

export const company = {
  legalName: "Edifique engenharia e construções LTDA",
  brandName: "Studio Edifique",
  siteUrl: SITE_URL,
  siteDisplayUrl: "studioedifique.com.br",
  city: "Itatiba",
  state: "SP",
  country: "Brasil",
  serviceRegion: "Itatiba e região",
  phoneDisplay: "(11) 99686-9406",
  phoneRaw: "11996869406",
  phoneInternational: "+5511996869406",
  whatsappUrl: "https://wa.me/5511996869406",
  phoneUrl: "tel:+5511996869406",
  email: "contato@studioedifique.com.br",
  emailUrl: "mailto:contato@studioedifique.com.br",
  instagramHandle: "@studio_edifique",
  instagramUrl: "https://www.instagram.com/studio_edifique/",
  logoFull,
  logoFullOnDark,
  logoIcon,
  shortHeadline: "Arquitetura e engenharia com direção técnica, leitura estética e visão de obra.",
  fullDescription:
    "O Studio Edifique desenvolve projetos arquitetônicos, interiores, fachadas e acompanhamento técnico de obras com foco em clareza construtiva, boa tomada de decisão e resultado coerente do conceito à execução.",
  metaDescription:
    "Studio Edifique: arquitetura e engenharia em Itatiba com projetos arquitetônicos, interiores, fachadas, administração de obra, fiscalização, laudos e construção.",
  seoKeywords: [
    "arquitetura em Itatiba",
    "engenharia civil em Itatiba",
    "projeto arquitetônico em Itatiba",
    "projeto de interiores",
    "administração de obra",
    "laudo técnico",
    "fiscalização de condomínios",
  ],
};

export const heroImages = {
  about: getImageFromFolder(
    "Projeto Arquitetonico - Residencial - Sete Lagos",
    "PISCINA_NOITE.jpg"
  ),
  portfolio: getImageFromFolder(
    "Projeto de interiores e Paisagismo - Residencial - MeR",
    "PISCINA_NOITE_R00_19 - Foto.jpg"
  ),
  team: getImageFromFolder(
    "Projeto fachada - Projeto comercial - Mocambo Sede",
    "FACHADA_2 - Foto.jpg"
  ),
  contact: getImageFromFolder("Adm obra - capital ville - obra 01", "20231208_121451.jpg"),
  blog: getImageFromFolder("Adm obra  - salão festas", "IMG_20260413_093549.jpg"),
};

export const services = [
  {
    icon: "villa",
    title: "Projeto arquitetônico",
    path: "/servicos/projeto-arquitetonico-itatiba",
    description:
      "Estudos e soluções completas para residências e espaços comerciais com forma, função e viabilidade construtiva.",
  },
  {
    icon: "chair_alt",
    title: "Projeto de interiores",
    path: "/servicos/projeto-interiores-itatiba",
    description:
      "Ambientes pensados para rotina, identidade visual, conforto e melhor aproveitamento de cada metro quadrado.",
  },
  {
    icon: "view_in_ar",
    title: "Renderização e modelagem 3D",
    description:
      "Imagens e simulações para antecipar decisões, validar acabamentos e comunicar o projeto com mais clareza.",
  },
  {
    icon: "engineering",
    title: "Administração de obra",
    path: "/servicos/administracao-obras-itatiba",
    description:
      "Acompanhamento técnico, organização de etapas, controle de execução e apoio à tomada de decisão no canteiro.",
  },
  {
    icon: "apartment",
    title: "Fiscalização para condomínios",
    description:
      "Suporte técnico para obras, reformas e intervenções em áreas condominiais com mais segurança documental.",
  },
  {
    icon: "fact_check",
    title: "Laudo e parecer técnico",
    path: "/servicos/laudo-tecnico-itatiba",
    description:
      "Análises técnicas para orientar regularizações, identificar patologias, registrar condições e fundamentar decisões.",
  },
  {
    icon: "construction",
    title: "Construção",
    description:
      "Execução alinhada ao projeto e ao planejamento, com foco em compatibilização, produtividade e qualidade final.",
  },
];

export const differentiators = [
  {
    icon: "architecture",
    title: "Visão integrada",
    description:
      "Arquitetura e engenharia atuando em conjunto para reduzir ruídos entre conceito, detalhamento e execução.",
  },
  {
    icon: "rule",
    title: "Decisão técnica",
    description:
      "Cada solução é avaliada considerando estética, construtibilidade, manutenção e desempenho no uso real.",
  },
  {
    icon: "lan",
    title: "Comunicação clara",
    description:
      "Modelagem, imagens e documentação ajudam o cliente a entender o projeto antes da obra avançar.",
  },
  {
    icon: "handyman",
    title: "Acompanhamento prático",
    description:
      "A presença técnica na obra reduz improvisos, evita retrabalho e melhora o controle de qualidade.",
  },
];

export const processSteps = [
  {
    title: "Diagnóstico e briefing",
    description:
      "Levantamos necessidades, rotina, prioridades e contexto da obra para direcionar soluções mais assertivas.",
  },
  {
    title: "Conceito e estudo técnico",
    description:
      "Transformamos o briefing em proposta com linguagem arquitetônica, lógica construtiva e compatibilização inicial.",
  },
  {
    title: "Detalhamento e visualização",
    description:
      "Aprofundamos o projeto com materiais, modelagem 3D e definições que reduzem incertezas na execução.",
  },
  {
    title: "Execução assistida",
    description:
      "Quando necessário, acompanhamos a obra para orientar etapas, validar entregas e manter o padrão esperado.",
  },
];

export const aboutFaqs = [
  {
    question: "O Studio Edifique atende apenas Itatiba?",
    answer:
      "A base de atuação está em Itatiba, mas a estrutura do trabalho permite atender demandas compatíveis em toda a região mediante análise prévia do escopo.",
  },
  {
    question: "Vocês trabalham somente com projeto ou também acompanham a obra?",
    answer:
      "Os serviços podem ser contratados de forma independente ou integrada, incluindo projeto, visualização 3D, administração de obra, fiscalização e construção.",
  },
  {
    question: "O projeto 3D substitui o projeto executivo?",
    answer:
      "Não. A modelagem 3D ajuda a visualizar e decidir melhor, enquanto os documentos técnicos orientam a execução com mais precisão no canteiro.",
  },
  {
    question: "Como o acompanhamento técnico gera economia?",
    answer:
      "A economia aparece na redução de retrabalhos, compras mal dimensionadas, escolhas incompatíveis e falhas de execução que custam caro ao longo da obra.",
  },
];

export const contactFaqs = [
  {
    question: "Como funciona o primeiro atendimento?",
    answer:
      "O primeiro contato pode ser feito por WhatsApp. A partir disso, organizamos o entendimento da demanda, do prazo desejado e do nível de apoio técnico necessário.",
  },
  {
    question: "Vocês compartilham o endereço do escritório no site?",
    answer:
      "Não. O endereço não é divulgado publicamente e é compartilhado apenas quando necessário ao andamento do atendimento.",
  },
  {
    question: "Quais tipos de cliente o Studio Edifique atende?",
    answer:
      "Atendemos clientes residenciais, comerciais e condomínios que precisem de projeto, regularização, acompanhamento técnico ou execução.",
  },
  {
    question: "É possível pedir orçamento para mais de um serviço?",
    answer:
      "Sim. Muitas demandas combinam projeto arquitetônico, interiores, fachadas e administração de obra em uma mesma proposta.",
  },
];

export const portfolioCategories = [
  {
    id: "arquitetonicos",
    label: "Projetos arquitetônicos",
    icon: "villa",
    description:
      "Projetos residenciais pensados para equilibrar identidade arquitetônica, fluidez de uso e coerência construtiva.",
    projects: [
      createProject({
        folderName: "Projeto Arquitetonico - Residencial - ReD",
        title: "Residencial ReD",
        coverFile: "ReD_7 - PRECIPITAÇÃO_8 - Foto.jpg",
        type: "Projeto arquitetônico residencial",
        scope: "Concepção arquitetônica e estudo volumétrico",
        focus: "Linguagem contemporânea, composição linear e presença formal marcante.",
        description:
          "Estudo residencial com leitura contemporânea, valorizando proporção, planos bem definidos e apresentação visual direta para validar conceito e volumetria.",
        highlights: ["Residencial", "Estudo autoral", "Visualização 3D"],
      }),
      createProject({
        folderName: "Projeto Arquitetonico - Residencial - Sete Lagos",
        title: "Residencial Sete Lagos",
        coverFile: "PISCINA_NOITE.jpg",
        type: "Projeto arquitetônico residencial",
        scope: "Projeto autoral com ênfase em lazer e ambientes integrados",
        focus: "Áreas sociais abertas, valorização da área externa e ambiência noturna.",
        description:
          "Proposta residencial que explora integração entre cozinha, lazer e paisagismo, com atenção especial à atmosfera criada pela iluminação e pelos espaços de convivência.",
        highlights: ["Residencial", "Área de lazer", "Integração"],
      }),
    ],
  },
  {
    id: "interiores",
    label: "Projetos de interiores",
    icon: "weekend",
    description:
      "Interiores e paisagismo concebidos para traduzir rotina, conforto, estética e melhor aproveitamento dos ambientes.",
    projects: [
      createProject({
        folderName: "Projeto de interiores e Paisagismo - Residencial - MeR",
        title: "Residencial MeR",
        coverFile: "PISCINA_NOITE_R00_19 - Foto.jpg",
        type: "Interiores e paisagismo residencial",
        scope: "Composição da área externa e ambientação de lazer",
        focus: "Conforto visual, iluminação e convivência ao ar livre.",
        description:
          "Projeto que articula mobiliário, vegetação, iluminação e apoio ao uso externo para criar uma experiência residencial elegante e acolhedora.",
        highlights: ["Paisagismo", "Lazer", "Interiores"],
      }),
      createProject({
        folderName: "Projeto Interiores - Projeto Comercial - Mocambo sede",
        title: "Mocambo Sede",
        coverFile: "JARDIM_INTERNO_DIA_14 - Foto.jpg",
        type: "Interiores comerciais",
        scope: "Ambientes internos para sede comercial",
        focus: "Recepção, encantamento e ambientação de experiência.",
        description:
          "Projeto comercial voltado para recepção, circulação e percepção de marca, buscando uma linguagem consistente entre acolhimento, apresentação e funcionalidade.",
        highlights: ["Comercial", "Recepção", "Experiência"],
      }),
      createProject({
        folderName: "Projeto Interiores - Sala de Jogos Kids",
        title: "Sala de Jogos Kids",
        coverFile: "NOITE.jpg",
        type: "Interiores residenciais",
        scope: "Espaço de recreação para jovens e crianças",
        focus: "Diversão, acolhimento e identidade visual lúdica.",
        description:
          "Ambiente projetado para estimular permanência e uso social, com leitura contemporânea e soluções que valorizam conforto, iluminação e atmosfera.",
        highlights: ["Lúdico", "Residencial", "Convivência"],
      }),
      createProject({
        folderName: "Projeto Interiores - Sala de Jogos",
        title: "Sala de Jogos",
        coverFile: "1_1 - Photo.jpg",
        type: "Interiores residenciais",
        scope: "Espaço de lazer interno",
        focus: "Integração visual, uso social e ambiência descontraída.",
        description:
          "Projeto de interior criado para ampliar o potencial de convivência do imóvel, com estética atual e leitura espacial organizada para o uso diário.",
        highlights: ["Lazer", "Interiores", "Ambiência"],
      }),
    ],
  },
  {
    id: "fachadas",
    label: "Projetos de fachadas",
    icon: "crop_square",
    description:
      "Fachadas que reforçam identidade visual, proporção e presença urbana para imóveis residenciais ou comerciais.",
    projects: [
      createProject({
        folderName: "Projeto fachada - Projeto comercial - Mocambo Sede",
        title: "Mocambo Sede - Fachada",
        coverFile: "FACHADA_1 - Photo.jpg",
        type: "Fachada comercial",
        scope: "Estudo volumétrico e linguagem externa",
        focus: "Imagem institucional, impacto visual e reconhecimento da marca.",
        description:
          "Solução de fachada para destacar o caráter comercial do empreendimento e fortalecer sua leitura arquitetônica desde a primeira aproximação.",
        highlights: ["Comercial", "Identidade", "Imagem"],
      }),
      createProject({
        folderName: "Projeto fachada - Projeto Comercial - Moc Adm",
        title: "Moc Adm - Fachada",
        coverFile: "FACHDA_FINAL.jpg",
        type: "Fachada comercial",
        scope: "Composição e linguagem para setor administrativo",
        focus: "Clareza formal, apresentação institucional e modernidade.",
        description:
          "Estudo voltado para consolidar uma imagem técnica e contemporânea, alinhada ao perfil do empreendimento e ao uso administrativo do espaço.",
        highlights: ["Comercial", "Corporativo", "Fachada"],
      }),
    ],
  },
  {
    id: "obras",
    label: "Administração de obras",
    icon: "engineering",
    description:
      "Registros de acompanhamento técnico com foco em execução, controle de etapas, qualidade e leitura prática do canteiro.",
    projects: [
      createProject({
        folderName: "Adm obra  - Mocambo Sede",
        title: "Mocambo Sede - Administração de Obra",
        coverFile: "IMG-20260421-WA0066.jpg",
        type: "Administração de obra comercial",
        scope: "Acompanhamento técnico e organização da execução",
        focus: "Evolução da obra, logística de etapas e controle visual do canteiro.",
        description:
          "Registro da administração de obra com atenção à organização das frentes de serviço, leitura global do empreendimento e apoio técnico às decisões executivas.",
        highlights: ["Comercial", "Acompanhamento", "Canteiro"],
      }),
      createProject({
        folderName: "Adm obra  - salão festas",
        title: "Salão de Festas - Administração de Obra",
        coverFile: "IMG_20260413_093549.jpg",
        type: "Administração de obra",
        scope: "Execução e acompanhamento de área de convivência",
        focus: "Etapas estruturais, concretagem e organização do processo construtivo.",
        description:
          "Acompanhamento técnico de obra voltado a espaço de convivência, com ênfase em leitura do canteiro, sequenciamento de serviços e qualidade da execução.",
        highlights: ["Concretagem", "Convivência", "Execução"],
      }),
      createProject({
        folderName: "Adm obra - capital ville - obra 01",
        title: "Capital Ville - Obra 01",
        coverFile: "20231208_121451.jpg",
        type: "Administração de obra residencial",
        scope: "Acompanhamento técnico do avanço construtivo",
        focus: "Compatibilização entre implantação, estrutura e acabamento futuro.",
        description:
          "Obra residencial com acompanhamento de etapas executivas, observação do desempenho do canteiro e suporte à tomada de decisão ao longo da construção.",
        highlights: ["Residencial", "Acompanhamento", "Execução"],
      }),
      createProject({
        folderName: "Adm obra - capital ville - Obra 02",
        title: "Capital Ville - Obra 02",
        coverFile: "IMG-20231222-WA0045.jpeg",
        type: "Administração de obra residencial",
        scope: "Controle técnico e leitura de progresso do canteiro",
        focus: "Organização de obra, monitoramento e consistência construtiva.",
        description:
          "Registro de acompanhamento técnico com foco em produtividade, qualidade de execução e entendimento claro do estágio construtivo da residência.",
        highlights: ["Residencial", "Progresso", "Gestão"],
      }),
      createProject({
        folderName: "Adm obra - teste slump",
        title: "Teste de Slump em Obra",
        coverFile: "20230714_122151.jpg",
        type: "Controle tecnológico",
        scope: "Registro técnico de ensaio e conferência de concreto",
        focus: "Validação da trabalhabilidade e apoio ao controle de qualidade.",
        description:
          "Conteúdo prático ligado ao acompanhamento técnico da concretagem, importante para documentar cuidados que impactam diretamente o desempenho estrutural.",
        highlights: ["Concreto", "Slump test", "Qualidade"],
      }),
    ],
  },
];

export const servicePages = landingContent.services;
export const caseStudies = landingContent.projects.map((detail) => ({
  ...portfolioCategories
    .flatMap((category) => category.projects)
    .find((project) => project.folderName === detail.folderName),
  ...detail,
}));

const articleAuthors = [
  {
    name: company.brandName,
    type: "Organization",
  },
];

export const teamMembers = [
  {
    name: "Sabrina Monticcelli",
    role: "Arquiteta e Urbanista",
    image: sabrinaProfile,
    description:
      "Atua no desenvolvimento de soluções arquitetônicas e de interiores com atenção à leitura estética, à funcionalidade dos ambientes e à experiência de uso.",
    credentials: ["Graduação em Arquitetura e Urbanismo - Universidade São Francisco (USF)"],
    highlights: ["Projeto arquitetônico", "Interiores", "Concepção estética"],
  },
  {
    name: "Gabriel Chagas",
    role: "Arquiteto e Urbanista | Engenheiro Civil",
    image: gabrielProfile,
    description:
      "Conecta arquitetura, engenharia e acompanhamento técnico de obra para orientar decisões com base em desempenho, compatibilização e viabilidade executiva.",
    credentials: [
      "Graduação em Arquitetura e Urbanismo - Universidade São Francisco (USF)",
      "Graduação em Engenharia Civil - Universidade São Francisco (USF)",
      "Pós-graduação em Inteligência Artificial - Unyleya Educacional",
      "Acústica de Salas - estudante especial de pós-graduação na Unicamp",
      "Tópicos Especiais em Construção IV - estudante especial de pós-graduação na Unicamp",
      "Acústica de Edificações - estudante especial de pós-graduação na Unicamp",
      "Curso de Perícia Judicial - CRECI-SP",
      "Curso Técnico em Transações Imobiliárias - EBRAE",
    ],
    highlights: ["Administração de obra", "Laudos", "Compatibilização técnica"],
  },
];

export const teamSectionIntro =
  "Uma equipe técnica enxuta, multidisciplinar e orientada por decisões que façam sentido no papel, na obra e no uso final do espaço.";

export const blogPosts = [
  {
    slug: "importancia-do-slump-test",
    category: "Controle tecnológico",
    title: "Slump test: por que o ensaio de abatimento protege a qualidade do concreto",
    excerpt:
      "Entenda como o slump test ajuda a controlar a trabalhabilidade do concreto, reduz falhas de execução e melhora a tomada de decisão no canteiro.",
    heroImage: getImageFromFolder("Adm obra - teste slump", "20230714_122151.jpg"),
    coverImage: getImageFromFolder("Adm obra - teste slump", "20230714_122151.jpg"),
    readingTime: "5 min de leitura",
    seoDescription:
      "Descubra por que o slump test é essencial para controlar a trabalhabilidade do concreto e evitar patologias durante a execução da obra.",
    keywords: [
      "slump test",
      "ensaio de abatimento do concreto",
      "controle tecnológico da obra",
      "qualidade do concreto",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Ajuda a validar a trabalhabilidade do concreto na chegada à obra.",
      "Evita decisões tomadas apenas por percepção visual da mistura.",
      "Contribui para rastreabilidade e controle de qualidade do processo.",
    ],
    sections: [
      {
        heading: "O que o slump test mede na prática",
        paragraphs: [
          "O slump test mede o abatimento do concreto fresco e, por consequência, fornece uma leitura objetiva sobre sua trabalhabilidade. Em obra, isso é importante porque pequenas diferenças no comportamento do material impactam diretamente lançamento, adensamento e acabamento.",
          "Quando o ensaio é tratado como rotina de controle, a equipe deixa de depender apenas da percepção informal do canteiro e passa a registrar um parâmetro que sustenta decisões técnicas com mais segurança.",
        ],
      },
      {
        heading: "Por que esse ensaio evita problemas",
        paragraphs: [
          "Concreto excessivamente seco pode dificultar o adensamento e aumentar o risco de vazios. Já um concreto muito fluido pode indicar perda de desempenho ou alterações inadequadas na mistura, principalmente quando alguém tenta corrigir trabalhabilidade adicionando água sem controle.",
          "O ensaio de abatimento não substitui todo o controle tecnológico, mas funciona como um filtro rápido e eficiente para identificar desvios antes que o material seja lançado e o problema vire retrabalho ou patologia futura.",
        ],
      },
      {
        heading: "Como incorporar o slump test à rotina da obra",
        paragraphs: [
          "O ideal é que o teste faça parte do protocolo de recebimento do concreto, com registro do traço solicitado, horário, fornecedor, resultado do abatimento e observações do lançamento.",
          "Essa simples disciplina melhora a rastreabilidade, ajuda a comparar ocorrências entre concretagens e fortalece a tomada de decisão do engenheiro responsável diante de qualquer variação inesperada.",
        ],
      },
    ],
    faqs: [
      {
        question: "O slump test garante sozinho a resistência do concreto?",
        answer:
          "Não. Ele avalia principalmente a consistência e a trabalhabilidade do concreto fresco. A resistência depende de outros fatores e de outros controles complementares.",
      },
      {
        question: "É correto adicionar água ao concreto na obra para ajustar o abatimento?",
        answer:
          "Essa prática não deve ser feita sem critério técnico, porque altera a relação água/cimento e pode comprometer o desempenho do material.",
      },
      {
        question: "Quando o teste deve ser realizado?",
        answer:
          "Preferencialmente no recebimento do concreto e antes do lançamento, seguindo o protocolo definido para cada concretagem.",
      },
    ],
  },
  {
    slug: "importancia-dos-espacadores",
    category: "Execução estrutural",
    title: "Espaçadores na obra: por que eles são essenciais para durabilidade e cobrimento",
    excerpt:
      "Os espaçadores costumam parecer detalhe, mas fazem diferença na posição das armaduras, no cobrimento e na vida útil da estrutura.",
    heroImage: getImageFromFolder("Adm obra  - salão festas", "IMG_20260413_093549.jpg"),
    coverImage: getImageFromFolder("Adm obra  - salão festas", "IMG_20260413_093549.jpg"),
    readingTime: "5 min de leitura",
    seoDescription:
      "Veja por que os espaçadores são fundamentais para garantir cobrimento, proteger as armaduras e aumentar a durabilidade da estrutura.",
    keywords: [
      "espaçadores na obra",
      "cobrimento de armadura",
      "durabilidade estrutural",
      "execução de concreto armado",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Mantêm a armadura na posição correta durante a concretagem.",
      "Ajudam a garantir o cobrimento previsto em projeto.",
      "Reduzem risco de corrosão prematura e patologias estruturais.",
    ],
    sections: [
      {
        heading: "O papel do espaçador no concreto armado",
        paragraphs: [
          "Espaçadores servem para manter as barras de aço afastadas da fôrma na medida prevista em projeto. Essa distância é o cobrimento, essencial para proteger a armadura e permitir que a estrutura trabalhe como foi calculada.",
          "Sem esse posicionamento correto, a armadura pode encostar na fôrma, ficar exposta demais ou perder alinhamento durante o lançamento do concreto.",
        ],
      },
      {
        heading: "O que acontece quando esse detalhe é ignorado",
        paragraphs: [
          "A ausência de espaçadores compromete a regularidade do cobrimento e abre espaço para problemas como corrosão da armadura, fissuras e perda de durabilidade da peça estrutural.",
          "Além disso, o improviso em obra costuma gerar uma falsa sensação de economia, mas aumenta o risco de correções posteriores que custam muito mais do que o cuidado preventivo.",
        ],
      },
      {
        heading: "Boa prática é detalhe bem executado",
        paragraphs: [
          "O uso correto de espaçadores precisa estar alinhado ao tipo de peça, à bitola da armadura, à agressividade do ambiente e ao posicionamento previsto no projeto estrutural.",
          "Quando o acompanhamento técnico observa esses detalhes, a qualidade da estrutura deixa de depender da sorte e passa a seguir um padrão consistente de execução.",
        ],
      },
    ],
    faqs: [
      {
        question: "Espaçador é apenas um acessório opcional?",
        answer:
          "Não. Ele é parte importante da boa execução porque contribui diretamente para o cobrimento e para a durabilidade da estrutura.",
      },
      {
        question: "Qualquer improviso pode substituir o espaçador?",
        answer:
          "Improvisos costumam gerar resultados inconsistentes. O ideal é utilizar elementos apropriados para a função e compatíveis com a peça executada.",
      },
      {
        question: "O cobrimento influencia a proteção do aço?",
        answer:
          "Sim. O cobrimento adequado ajuda a proteger a armadura contra agentes agressivos e aumenta a vida útil da estrutura.",
      },
    ],
  },
  {
    slug: "diferenca-entre-engenheiro-e-arquiteto-na-obra",
    category: "Gestão de obra",
    title: "Engenheiro e arquiteto na obra: diferenças, responsabilidades e atuação conjunta",
    excerpt:
      "Entenda como arquiteto e engenheiro contribuem de formas complementares na obra e por que essa integração melhora o resultado final.",
    heroImage: getImageFromFolder("Adm obra - capital ville - obra 01", "20231208_121451.jpg"),
    coverImage: getImageFromFolder("Adm obra - capital ville - obra 01", "20231208_121451.jpg"),
    readingTime: "6 min de leitura",
    seoDescription:
      "Saiba a diferença entre engenheiro e arquiteto na obra, como cada profissional atua e por que a integração entre ambos reduz falhas.",
    keywords: [
      "diferença entre engenheiro e arquiteto",
      "engenheiro na obra",
      "arquiteto na obra",
      "gestão integrada de projeto",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Arquiteto e engenheiro têm atribuições complementares.",
      "A integração entre projeto e execução reduz conflitos na obra.",
      "O cliente ganha mais clareza técnica e menos improviso.",
    ],
    sections: [
      {
        heading: "O arquiteto olha espaço, uso e linguagem",
        paragraphs: [
          "O arquiteto trabalha a organização espacial, a relação entre ambientes, a leitura estética do projeto e a experiência de uso. É quem traduz necessidades em soluções formais e funcionais compatíveis com o cotidiano do cliente.",
          "Na obra, essa atuação ajuda a preservar a intenção do projeto, orientar acabamentos e manter coerência entre conceito, proporção e qualidade percebida do espaço final.",
        ],
      },
      {
        heading: "O engenheiro estrutura a viabilidade e a execução",
        paragraphs: [
          "O engenheiro atua com foco em desempenho técnico, dimensionamentos, segurança, compatibilização executiva e controle da obra. Ele observa como cada solução se comporta na prática e como a execução precisa ser organizada para atender ao que foi definido.",
          "Isso inclui leitura do canteiro, controle de etapas, avaliação de materiais e suporte técnico para que a obra avance com consistência e menos improvisos.",
        ],
      },
      {
        heading: "Por que a atuação conjunta é mais eficiente",
        paragraphs: [
          "Quando arquitetura e engenharia trabalham em conjunto, o cliente ganha um processo mais completo: a solução nasce com intenção estética, mas já considera viabilidade e desempenho.",
          "Essa integração reduz conflitos entre projeto e execução, melhora a comunicação com a equipe de obra e contribui para decisões mais econômicas e melhor fundamentadas.",
        ],
      },
    ],
    faqs: [
      {
        question: "Arquiteto e engenheiro fazem exatamente a mesma coisa?",
        answer:
          "Não. Existem áreas de interseção, mas cada profissional possui competências e responsabilidades específicas dentro do processo.",
      },
      {
        question: "Uma obra pequena também se beneficia dessa integração?",
        answer:
          "Sim. Mesmo em obras menores, a combinação entre boa solução espacial e boa orientação técnica tende a reduzir erros e retrabalhos.",
      },
      {
        question: "A integração melhora o orçamento?",
        answer:
          "Sim. Quando decisões são tomadas com mais base técnica desde o início, fica mais fácil evitar desperdícios e incompatibilidades.",
      },
    ],
  },
  {
    slug: "importancia-do-projeto-arquitetonico",
    category: "Projeto",
    title: "Projeto arquitetônico: por que planejar antes de construir economiza tempo e dinheiro",
    excerpt:
      "Um bom projeto arquitetônico organiza decisões, reduz improvisos e ajuda o cliente a construir com mais segurança.",
    heroImage: getImageFromFolder(
      "Projeto Arquitetonico - Residencial - Sete Lagos",
      "PISCINA_NOITE_7 - Foto.jpg"
    ),
    coverImage: getImageFromFolder(
      "Projeto Arquitetonico - Residencial - Sete Lagos",
      "PISCINA_NOITE_7 - Foto.jpg"
    ),
    readingTime: "5 min de leitura",
    seoDescription:
      "Entenda por que o projeto arquitetônico é essencial para evitar retrabalho, organizar decisões e construir com mais eficiência.",
    keywords: [
      "projeto arquitetônico",
      "importância do projeto arquitetônico",
      "planejamento da obra",
      "economia na construção",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Organiza as decisões antes da fase mais cara da obra.",
      "Melhora compatibilização entre estética, uso e execução.",
      "Reduz mudanças improvisadas durante a construção.",
    ],
    sections: [
      {
        heading: "Projeto não é custo extra, é organização de decisão",
        paragraphs: [
          "Quando a obra começa sem um projeto arquitetônico bem definido, muitas escolhas são empurradas para o canteiro. O problema é que decidir tarde costuma ser mais caro, mais lento e menos preciso.",
          "O projeto antecipa a lógica dos ambientes, a relação entre áreas, a volumetria da edificação e a base para compatibilizar outras disciplinas técnicas.",
        ],
      },
      {
        heading: "Improviso custa mais do que parece",
        paragraphs: [
          "Mudanças feitas durante a obra raramente afetam apenas um ponto. Elas podem impactar estrutura, instalações, esquadrias, paginação, materiais e cronograma.",
          "Quando o projeto está maduro antes do início da execução, as decisões deixam de acontecer sob pressão e passam a seguir uma linha de raciocínio mais segura e econômica.",
        ],
      },
      {
        heading: "Visualização e clareza ajudam o cliente",
        paragraphs: [
          "Além da documentação técnica, a visualização em 3D ajuda o cliente a entender o espaço e validar escolhas com mais confiança. Isso reduz dúvidas e melhora a comunicação entre todos os envolvidos.",
          "Planejar bem não significa engessar a obra, mas sim criar uma base sólida para que eventuais ajustes sejam pontuais, conscientes e tecnicamente coerentes.",
        ],
      },
    ],
    faqs: [
      {
        question: "O projeto arquitetônico é importante mesmo em reformas?",
        answer:
          "Sim. Em reformas, ele ajuda ainda mais porque lida com limitações existentes e evita decisões mal compatibilizadas.",
      },
      {
        question: "Modelagem 3D ajuda no projeto arquitetônico?",
        answer:
          "Ajuda muito na compreensão do espaço e na validação de materiais, proporções e ambiência antes da obra.",
      },
      {
        question: "Projeto pronto elimina todos os ajustes de obra?",
        answer:
          "Nenhum processo elimina completamente ajustes, mas um projeto bem desenvolvido reduz drasticamente improvisos e correções caras.",
      },
    ],
  },
  {
    slug: "economia-gerada-por-sondagem-de-solo",
    category: "Fundação e estrutura",
    title: "Sondagem de solo: a economia invisível que evita fundações inadequadas",
    excerpt:
      "A sondagem de solo ajuda a escolher fundações coerentes com o terreno e evita gastos desnecessários por erro de premissa.",
    heroImage: getImageFromFolder("Adm obra - capital ville - obra 01", "20231208_112214.jpg"),
    coverImage: getImageFromFolder("Adm obra - capital ville - obra 01", "20231208_112214.jpg"),
    readingTime: "5 min de leitura",
    seoDescription:
      "Saiba como a sondagem de solo gera economia ao evitar fundações superdimensionadas, soluções inseguras e correções caras na obra.",
    keywords: [
      "sondagem de solo",
      "economia na fundação",
      "estudo geotécnico",
      "fundação adequada",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Reduz decisões baseadas em suposição sobre o terreno.",
      "Ajuda a definir fundações proporcionais à realidade geotécnica.",
      "Evita custos com reforços, correções e atrasos posteriores.",
    ],
    sections: [
      {
        heading: "Por que conhecer o solo muda toda a lógica da fundação",
        paragraphs: [
          "A fundação depende das características reais do terreno, não apenas da aparência superficial do lote. A sondagem fornece dados que permitem ao projeto estrutural escolher soluções coerentes com a capacidade de suporte e com o comportamento do solo.",
          "Sem essa base, a obra corre o risco de adotar soluções exageradas por medo ou, pior, soluções insuficientes por desconhecimento.",
        ],
      },
      {
        heading: "Onde a economia realmente aparece",
        paragraphs: [
          "A economia gerada pela sondagem não está apenas em gastar menos concreto ou aço. Ela aparece principalmente em evitar uma decisão errada de fundação, que poderia gerar recalques, reforços ou atraso de cronograma.",
          "Com informação geotécnica, a estrutura deixa de ser uma aposta e passa a seguir critérios técnicos mais consistentes.",
        ],
      },
      {
        heading: "Planejamento técnico é melhor do que correção cara",
        paragraphs: [
          "Muitos problemas de obra surgem quando decisões fundamentais são tomadas com informação incompleta. A sondagem reduz esse risco logo no início do processo.",
          "Quando o projeto nasce com base em dados confiáveis, a obra ganha mais previsibilidade, o orçamento fica mais coerente e o nível de improviso cai significativamente.",
        ],
      },
    ],
    faqs: [
      {
        question: "Toda obra precisa de sondagem?",
        answer:
          "A necessidade depende do porte e das características do empreendimento, mas em muitos casos a sondagem é uma etapa estratégica e altamente recomendável.",
      },
      {
        question: "A sondagem encarece o início da obra?",
        answer:
          "Ela representa um investimento inicial, mas costuma reduzir riscos e evitar custos muito maiores decorrentes de decisões mal fundamentadas.",
      },
      {
        question: "Sondagem substitui o projeto estrutural?",
        answer:
          "Não. Ela fornece dados essenciais para que o projeto estrutural possa ser desenvolvido de forma coerente com o terreno.",
      },
    ],
  },
  {
    slug: "economia-com-administracao-de-obra-por-engenheiro",
    category: "Gestão de obra",
    title:
      "Administração de obra por engenheiro: economia gerada por controle, sequência e decisão técnica",
    excerpt:
      "A administração de obra reduz desperdícios ao organizar etapas, conferir execução e apoiar decisões com base técnica.",
    heroImage: getImageFromFolder("Adm obra  - Mocambo Sede", "IMG-20260421-WA0066.jpg"),
    coverImage: getImageFromFolder("Adm obra  - Mocambo Sede", "IMG-20260421-WA0066.jpg"),
    readingTime: "6 min de leitura",
    seoDescription:
      "Descubra como a administração de obra por engenheiro ajuda a economizar com menos retrabalho, melhor logística e decisões técnicas mais seguras.",
    keywords: [
      "administração de obra",
      "engenheiro na obra",
      "economia na construção",
      "controle de obra",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "Melhora sequenciamento e leitura do canteiro.",
      "Reduz compras precipitadas e retrabalho por falha de execução.",
      "Apoia decisões de campo com critério técnico.",
    ],
    sections: [
      {
        heading: "A obra perde dinheiro quando funciona no improviso",
        paragraphs: [
          "Boa parte do desperdício em obra não vem apenas de material. Ele aparece em etapas fora de ordem, serviços refeitos, compras mal planejadas, equipes paradas e decisões tomadas tarde demais.",
          "A administração de obra por engenheiro ajuda a enxergar essa dinâmica com mais clareza e transformar o canteiro em um processo menos reativo.",
        ],
      },
      {
        heading: "Controle técnico gera economia operacional",
        paragraphs: [
          "Quando há acompanhamento, a obra passa a ter leitura de avanço, conferência de execução e maior previsibilidade para próximas etapas. Isso reduz perdas por desorganização e melhora o uso do orçamento.",
          "Além disso, problemas pequenos são identificados cedo, antes de contaminarem outras frentes de serviço ou exigirem correções mais complexas.",
        ],
      },
      {
        heading: "Engenharia também é gestão do processo",
        paragraphs: [
          "A presença do engenheiro não se limita a aprovar ou reprovar uma etapa. Ela ajuda a organizar o processo construtivo como um todo, conectar projeto e campo e orientar decisões com foco em desempenho e custo global.",
          "Esse tipo de administração faz diferença especialmente em obras com várias interfaces, cronograma apertado ou alto potencial de retrabalho.",
        ],
      },
    ],
    faqs: [
      {
        question: "Administração de obra vale a pena em obras residenciais?",
        answer:
          "Sim. Mesmo obras residenciais podem sofrer com desperdícios relevantes quando não há coordenação adequada das etapas.",
      },
      {
        question: "O engenheiro substitui o mestre de obras?",
        answer:
          "Não. As funções são diferentes e complementares. A administração técnica ajuda a orientar o processo e a tomada de decisão.",
      },
      {
        question: "Onde a economia aparece com mais frequência?",
        answer:
          "Principalmente em redução de retrabalho, melhor compra de insumos, sequência mais eficiente de serviços e menor improviso em campo.",
      },
    ],
  },
  {
    slug: "projeto-estrutural-sem-superdimensionamento",
    category: "Estrutura",
    title:
      "Projeto estrutural: economia com a estrutura necessária, sem superdimensionamento e sem risco",
    excerpt:
      "Um bom projeto estrutural busca equilíbrio: nem excesso de material, nem solução subdimensionada que comprometa segurança e desempenho.",
    heroImage: getImageFromFolder("Adm obra  - salão festas", "IMG-20260413-WA0011.jpeg"),
    coverImage: getImageFromFolder("Adm obra  - salão festas", "IMG-20260413-WA0011.jpeg"),
    readingTime: "6 min de leitura",
    seoDescription:
      "Entenda como o projeto estrutural bem dimensionado gera economia sem cair no superdimensionamento nem comprometer segurança e desempenho.",
    keywords: [
      "projeto estrutural",
      "superdimensionamento",
      "economia estrutural",
      "dimensionamento de estrutura",
    ],
    author: articleAuthors,
    publishedLabel: "Conteúdo técnico Studio Edifique",
    highlights: [
      "A estrutura deve responder ao que a obra realmente precisa.",
      "Superdimensionar encarece sem necessariamente melhorar o resultado.",
      "Subdimensionar cria risco, correções e perda de desempenho.",
    ],
    sections: [
      {
        heading: "Estrutura boa é estrutura coerente",
        paragraphs: [
          "Projeto estrutural eficiente não é o que usa mais concreto ou mais aço. É o que responde corretamente às cargas, ao sistema construtivo, ao solo e à arquitetura prevista.",
          "Quando o dimensionamento é coerente, a obra evita exageros desnecessários e, ao mesmo tempo, não assume riscos por falta de capacidade estrutural.",
        ],
      },
      {
        heading: "O custo do excesso também precisa ser lido",
        paragraphs: [
          "Superdimensionamento costuma ser confundido com segurança, mas ele também tem custo. Excesso de material impacta orçamento, logística, execução e até outras decisões do projeto.",
          "A boa engenharia procura o ponto de equilíbrio entre desempenho, segurança e racionalidade construtiva.",
        ],
      },
      {
        heading: "Projeto compatível reduz desperdício global",
        paragraphs: [
          "Quando estrutura, arquitetura e condições de obra conversam entre si, o resultado tende a ser mais limpo e mais econômico. Isso evita surpresas em execução e melhora a previsibilidade do empreendimento.",
          "A economia certa não é cortar indiscriminadamente, mas usar exatamente o que a solução precisa para funcionar bem.",
        ],
      },
    ],
    faqs: [
      {
        question: "Superdimensionar deixa a obra mais segura?",
        answer:
          "Segurança depende de dimensionamento correto e coerente com as normas, e não simplesmente de excesso de material.",
      },
      {
        question: "É possível economizar na estrutura sem comprometer desempenho?",
        answer:
          "Sim. A economia técnica vem da adequação do sistema estrutural às necessidades reais da obra e às condições do terreno e do projeto.",
      },
      {
        question: "Arquitetura influencia o projeto estrutural?",
        answer:
          "Sim. Vãos, volumetria, distribuição de cargas e soluções espaciais influenciam diretamente a estrutura e precisam ser compatibilizados.",
      },
    ],
  },
];

export const featuredBlogPost = blogPosts[0];

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": getSiteUrl("/#organization"),
  name: company.brandName,
  legalName: company.legalName,
  description: company.fullDescription,
  email: company.email,
  telephone: company.phoneInternational,
  areaServed: company.serviceRegion,
  sameAs: [company.instagramUrl],
  url: company.siteUrl,
  image: getAssetUrl(company.logoFull),
  logo: getAssetUrl(company.logoIcon),
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: company.phoneInternational,
      email: company.email,
      areaServed: "BR",
      availableLanguage: ["pt-BR"],
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: company.city,
    addressRegion: company.state,
    addressCountry: "BR",
  },
});

export const createWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: company.brandName,
  description: company.metaDescription,
  inLanguage: "pt-BR",
  url: company.siteUrl,
});

export const createBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: getSiteUrl(item.path),
  })),
});

export const createFaqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const createArticleSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.seoDescription,
  image: getAssetUrl(post.heroImage),
  articleSection: post.category,
  author: {
    "@type": "Organization",
    name: company.brandName,
  },
  publisher: {
    "@type": "Organization",
    name: company.brandName,
    logo: {
      "@type": "ImageObject",
      url: getAssetUrl(company.logoIcon),
    },
  },
  mainEntityOfPage: getSiteUrl(`/blog/${post.slug}`),
  keywords: post.keywords.join(", "),
});
